import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  CreditCardOutlined,
  ThunderboltOutlined,
  WalletOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { Button, Modal, Space, Typography } from "antd";

const { Title, Text } = Typography;

/**
 * InsufficientCreditsModal Component
 * Displayed when user tries to perform an action without enough credits
 *
 * CRITICAL: This modal blocks the action until credits are added.
 * User cannot retry the action until they navigate to billing and add credits.
 */
const InsufficientCreditsModal = ({
  visible,
  onClose,
  requiredCredits,
  availableCredits,
  featureName,
  perCreditDisplay,
}) => {
  const navigate = useNavigate();

  const handleBuyCredits = () => {
    onClose();
    // Navigate to billing tab in account page
    navigate("/app/account?tab=billing");
  };

  const shortfall = Math.max(0, requiredCredits - availableCredits);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={440}
      closable={true}
      maskClosable={false} // Prevent accidental close
      closeIcon={<CloseOutlined style={{ fontSize: "16px", color: "#8c8c8c" }} />}
      styles={{
        body: { padding: "0" },
        content: { borderRadius: "16px", overflow: "hidden" },
      }}
    >
      <div style={{ padding: "32px 28px" }}>
        {/* Warning Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "24px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: "linear-gradient(135deg, #fa8c16 0%, #faad14 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow: "0 8px 24px rgba(250, 140, 22, 0.3)",
            }}
          >
            <WarningFilled style={{ fontSize: "28px", color: "#ffffff" }} />
          </div>

          <Title level={4} style={{ marginBottom: "8px", color: "#ad4e00" }}>
            Insufficient Credits
          </Title>

          <Text style={{ color: "#873800", fontSize: "14px" }}>
            You don't have enough credits to use this feature.
          </Text>
        </div>

        {/* Feature Info */}
        <div
          style={{
            background: "#f8fafc",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "20px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <ThunderboltOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
            <div>
              <Text strong style={{ display: "block", fontSize: "14px", color: "#1f2937" }}>
                {featureName || "This Feature"}
              </Text>
              <Text style={{ fontSize: "12px", color: "#6b7280" }}>
                Requires {requiredCredits} credits to use
              </Text>
            </div>
          </div>
        </div>

        {/* Credit Stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              background: "#fef2f2",
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center",
              border: "1px solid #fecaca",
            }}
          >
            <div style={{ fontSize: "28px", fontWeight: 700, color: "#dc2626" }}>
              {shortfall}
            </div>
            <div style={{ fontSize: "12px", color: "#991b1b", marginTop: "4px", fontWeight: 500 }}>
              Credits Needed
            </div>
          </div>
          <div
            style={{
              background: "#eff6ff",
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center",
              border: "1px solid #bfdbfe",
            }}
          >
            <div style={{ fontSize: "28px", fontWeight: 700, color: "#2563eb" }}>
              {availableCredits}
            </div>
            <div style={{ fontSize: "12px", color: "#1e40af", marginTop: "4px", fontWeight: 500 }}>
              Your Balance
            </div>
          </div>
        </div>

        {/* Message */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "24px",
            padding: "12px 16px",
            background: "#f1f5f9",
            borderRadius: "8px",
          }}
        >
          <Text style={{ color: "#475569", fontSize: "13px", lineHeight: "1.6" }}>
            Please add credits to continue using {featureName || "this feature"}.
            Your progress will be saved.
          </Text>
        </div>

        {/* Action Buttons */}
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Button
            type="primary"
            size="large"
            icon={<WalletOutlined />}
            onClick={handleBuyCredits}
            style={{
              width: "100%",
              height: "52px",
              fontSize: "16px",
              fontWeight: 600,
              background: "linear-gradient(135deg, #1890ff 0%, #0050b3 100%)",
              border: "none",
              borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(24, 144, 255, 0.35)",
            }}
          >
            Add Credits Now
          </Button>
          <Button
            size="large"
            onClick={onClose}
            style={{
              width: "100%",
              height: "46px",
              fontSize: "14px",
              borderRadius: "12px",
              color: "#6b7280",
              borderColor: "#d1d5db",
            }}
          >
            Cancel
          </Button>
        </Space>

        {/* Pricing Note */}
        <div
          style={{
            textAlign: "center",
            marginTop: "20px",
            paddingTop: "16px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Text
            style={{
              fontSize: "12px",
              color: "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
          >
            <CreditCardOutlined />
            Starting at just {perCreditDisplay || "\u20B90.75"} per credit
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default InsufficientCreditsModal;
